//#region [IMPORT]

// === THIRD-PARTY ===
import React, { createContext, useContext, useEffect, useState  } from 'react';
import { Buffer } from 'buffer';
import { useNavigate } from 'react-router-dom';

// === STYLE / MATERIAL UI ===
import { useTheme } from '@mui/material/styles';
import {
  Alert,
  Box,
  Button,
  Divider,
  TextField,
  TableContainer,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Snackbar,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import PublishIcon from '@mui/icons-material/Publish';
import DoneIcon from '@mui/icons-material/Done';
import Download from '@mui/icons-material/GetApp';

import './index.css';

// === COMPONENTS ===
import { ProcessList } from './ProcessList/ProcessList';
import { Spacer } from 'components/Spacer';
import { AppContext  } from 'pages/App/components/AppContext/AppContext';

// === API ===
import { prokeyApi } from 'api/prokey';

//#endregion

//-----------------------------------------------------------------
function toBase64(file) {
  //-----------------------------------------------------------------
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result.slice(28));
    reader.onerror = (error) => reject(error);
  });
}

//-----------------------------------------------------------------
export function OcrOnDemand() {
  //-----------------------------------------------------------------

  //#region [VALUES]
  
  const { user_details } = useContext( AppContext );

  const theme        = useTheme();
  const navigate     = useNavigate();

  const [ success        , setSuccess        ] = useState(false);
  const [ loading        , setLoading        ] = useState(false);
  const [ errMessage     , setErrMessage     ] = useState('');
  const [ errOpen        , setErrOpen        ] = useState(false);
  const [ uploadProgress , setUploadProgress ] = useState(0);

  const [ tasks      , setTasks     ] = useState( [] );
  const [ start_date , setStartDate ] = useState( null );
  const [ process    , setProcess   ] = useState( null );

  const hasPermission = user_details.roles?.some((item) => item.RoleName === 'submitter');

  let page = localStorage.getItem('display_page');
  if( !page ) page = 'verify';

  //#endregion

  //#region [STYLES]

  const _styles = {
    CONTAINER: {
      height    : `calc(100% - 5rem)`,
      padding   : '1rem',
      overflow  : 'hidden',
      'z-index' : '0',
      backgroundColor : theme.palette.background.secondary[theme.palette.mode],
    },
    BATCHES_CONTAINER: {
      display : 'flex',
      height  : 'calc( 100% - 9rem )',
      borderRadius: '1rem',
      backgroundColor: theme.palette.background.primary[theme.palette.mode],
    },
  }

  // ATTRIBUTES

  const CONTAINER = {
    sx : _styles.CONTAINER,
  }

  const BATCHES_CONTAINER = {
    sx : _styles.BATCHES_CONTAINER,
  }

  //#endregion

  //#region [EFFECT]

  useEffect(() => {
    if (start_date === null) {
      var tzoffset = new Date().getTimezoneOffset() * 60000; //offset in milliseconds
      const d = new Date(Date.now() - tzoffset);
      const dt_str = d.toISOString().substring(0, 16);
      setStartDate( dt_str );
    }

    refreshTasks();

    const ocrOdTaskRefresher = setInterval(() => {
      refreshTasks();
    }, 5000);

    return () => {
      clearInterval(ocrOdTaskRefresher);
    };
  }, [start_date]);

  //#endregion

  //#region [EVENTS]

  //-----------------------------------------------------------------
  async function onProgressHandler(progressEvent) {
    //-----------------------------------------------------------------
    var percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
    setUploadProgress(percentCompleted);
  };

  //-----------------------------------------------------------------
  function onChange_Process(value) {
    //-----------------------------------------------------------------
    setProcess( value );
  }

  //-----------------------------------------------------------------
  async function handleUpload(event) {
    //-----------------------------------------------------------------
    // reset
    setSuccess(false);
    setLoading(true);

    // reset
    if (event.target.files) {
      let sendFiles = [];
      for (let file of event.target.files) {
        sendFiles.push({ name: file.name, data: await toBase64(file) });
      }
      prokeyApi.postOcrodBatch(process, sendFiles, onProgressHandler).then((res) => {
        if (res.status === 200) {
          // success
          setSuccess(true);
          setLoading(false);

          setTimeout(() => {
            refreshTasks();
          }, '200');
        } else {
          // failure
          setSuccess(false);
          setLoading(false);

          setErrMessage('Could not upload file');
          setErrOpen(true);
        }
      });
    }
  };

  //-----------------------------------------------------------------
  function onClick_Download(batchId) {
    //-----------------------------------------------------------------
    prokeyApi
      .getOcrodBatch(process, batchId)
      .then((res) => {
        // Create blob link to download
        let blob = Buffer.from(res.data.resultArchive, 'base64');
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${batchId}.zip`);

        // Append to html link element page
        document.body.appendChild(link);

        // Start download
        link.click();

        // Clean up and remove the link
        link.parentNode.removeChild(link);
      })
      .catch((err) => {
        console.log(err);
      });
  }

  //-----------------------------------------------------------------
  async function refreshTasks() {
    //-----------------------------------------------------------------
    try {
      if ( !start_date ) return;

      const localDate = new Date( start_date ).getTime();
      const tzoffset  = new Date().getTimezoneOffset() * 60000; //offset in milliseconds
      const utcDate   = new Date( localDate - tzoffset );
      const batchList = await prokeyApi.getOcrodBatchList(utcDate.toISOString());
      if (batchList.data) {
        setTasks( batchList.data );
      }
    } catch (error) {
      console.warn( error );
    }
  }

  if( user_details.roles.length === 0 ) {
    return null;
  }

  if( !hasPermission ) {
    navigate( '/' + page );
  }

  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function displayError() {
    //-----------------------------------------------------------------
    return (
      <Snackbar open={errOpen} autoHideDuration={6000} onClose={handleErrClose}>
        <Alert onClose={handleErrClose} severity='error'>
          {errMessage}
        </Alert>
      </Snackbar>
    );
  };

  //-----------------------------------------------------------------
  function handleErrClose(event, reason) {
    //-----------------------------------------------------------------
    if (reason === 'clickaway') {
      return;
    }
    setErrOpen(false);
  };

  //-----------------------------------------------------------------
  function uploadButton() {
    //-----------------------------------------------------------------
    return (
      <Box
        sx={{
          marginRight: theme.spacing(1),
          height: '4rem',
          width: '15rem',
        }}
      >
        <input
          accept='image/*,.pdf'
          style={{
            display: 'none',
          }}
          id='contained-button-file'
          multiple
          type='file'
          onChange={handleUpload}
        />
        <label htmlFor='contained-button-file'>
          <Button
            variant='contained'
            name='upload_batch_btn'
            color='primary'
            component='span'
            sx={{
              marginRight: theme.spacing(1),
              height: '4rem',
              width: '15rem',
              backgroundColor: success ? theme.palette.success.main : theme.palette.primary.main,
              '&:hover': {
                backgroundColor: success ? theme.palette.success.dark : theme.palette.primary.main.dark,
              },
            }}
            disabled={loading}
            endIcon={success ? <DoneIcon /> : <PublishIcon />}
          >
            Upload Batch
          </Button>
        </label>
      </Box>
    );
  };

  //-----------------------------------------------------------------
  function tasksSinceDatePicker() {
    //-----------------------------------------------------------------
    return (
        <form noValidate style={{
          marginLeft: 'auto',
          height: '4rem',
          width: '20rem',
        }}>
          <TextField
            id='datetime-local'
            name='select_batches_since_frm'
            label='View Batches Since'
            type='datetime-local'
            value={start_date}
            sx={{
              marginLeft: 'auto',
              height: '4rem',
              width: '20rem',
            }}
            InputLabelProps={{
              shrink: true,
            }}
            onChange={(event) => {
              setStartDate(event.target.value);
            }}
          />
        </form>
    );
  };

  function displayInstructions() {
    return (
      <Box sx={{
        display         : 'flex'   ,
        padding         : '1rem'   ,
        borderRadius    : '1rem'   ,
        alignItems      : 'center' ,
        backgroundColor : theme.palette.background.primary[theme.palette.mode],

      }}>
        <Typography>
          Paradatec recommends using the <strong> Paradatec API </strong> for all types of testing, such as with an application like Postman. <br/><br/>
          This also enables sending additional information such as comparison data to use in the Analytics step.
        </Typography>
        <Spacer />
        <Divider orientation="vertical" />
        <Spacer />
        <Typography>
          The upload function on this page is intended for low volume testing of classification, versioning and data extraction. When using this function, please note:
          <ul style={{marginBottom: '1rem', paddingLeft:'2rem'}}>
            <li>Use PDF format only.</li>
            <li>The maximum PDF document package upload is 50 MB.</li>
            <li>When the upload is complete, the Upload Batch button will change from blue to green. If it does not change to green within a minute, check the size of your document package and format.</li>
          </ul>
        </Typography>
      </Box>
    );
  }

  //-----------------------------------------------------------------
  function controls() {
    //-----------------------------------------------------------------
    return (
      <Box
        sx={{
          display         : 'flex'   ,
          height          : '8rem'   ,
          paddingLeft     : '1rem'   ,
          paddingRight    : '1rem'   ,
          marginBottom    : '1rem'   ,
          borderRadius    : '1rem'   ,
          alignItems      : 'center' ,
          backgroundColor : theme.palette.background.primary[theme.palette.mode],
        }}
      >
        <ProcessList onChange={onChange_Process} />
        {uploadButton()}
        {tasksSinceDatePicker()}
      </Box>
    );
  };

  //-----------------------------------------------------------------
  function statusStr(task) {
    //-----------------------------------------------------------------
    const dispValue = task.CurrentStatus ? task.CurrentStatus : '';
    if (dispValue === 'Inactive') return 'Complete';
    else return 'Processing';
  };

  //-----------------------------------------------------------------
  function progressBar(task) {
    //-----------------------------------------------------------------
    if (task.CurrentStatus === 'Inactive')
      return (
        <CircularProgress
          sx={{
            color: theme.palette.success.main,
            height: theme.spacing(1),
            width: theme.spacing(1),
          }}
          variant='determinate'
          size={35}
          value={100}
        />
      );
    else
      return (
        <CircularProgress
          sx={{
            height: theme.spacing(1),
          }}
          size={35}
        />
      );
  };

  //-----------------------------------------------------------------
  function timeStartStr(task) {
    //-----------------------------------------------------------------
    const dispValue = task.UploadTime ? new Date(task.UploadTime).toLocaleString() : '-';
    return dispValue;
  };

  //-----------------------------------------------------------------
  function timeEndStr(task) {
    //-----------------------------------------------------------------
    const dispValue = task.CompletionTime ? new Date(task.CompletionTime).toLocaleString() : '-';
    return dispValue;
  };

  //-----------------------------------------------------------------
  function timeDuration(task) {
    //-----------------------------------------------------------------
    const completionTime = task.CompletionTime ? task.CompletionTime : '';
    const uploadTime = task.UploadTime ? task.UploadTime : '';
    if (completionTime && uploadTime) {
      let timeDiffSecs = (new Date(task.CompletionTime).getTime() - new Date(task.UploadTime).getTime()) / 1000;
      let days = Math.floor(timeDiffSecs / 86400);
      let hours = Math.floor((timeDiffSecs % 86400) / 3600);
      let minutes = Math.floor((timeDiffSecs % 3600) / 60);
      let seconds = Math.floor(timeDiffSecs % 60);

      let dispVal = '';
      if (days) dispVal += `${days}d, `;
      if (days || hours) dispVal += `${hours}h, `;
      if (days || hours || minutes) dispVal += `${minutes}m, `;
      dispVal += `${seconds}s`;

      return dispVal;
    } else return '-';
  };

  //-----------------------------------------------------------------
  function taskDownload(task) {
    //-----------------------------------------------------------------
    const disabled = task.CurrentStatus !== 'Inactive';
    return (
      <Button
        variant='contained'
        color='primary'
        disabled={disabled}
        startIcon={<Download />}
        onClick={() => onClick_Download(task.BatchId)}
      >
        Zip
      </Button>
    );
  };

  //-----------------------------------------------------------------
  function displayTasks() {
    //-----------------------------------------------------------------
    let result = [];
    const ids = Object.keys(tasks);
    for (const task_id of ids.reverse()) {
      const task = tasks[task_id];
      result.push(
        <TableRow key={task_id} id={task_id}>
          <TableCell key={1}>{task?.BatchId}</TableCell>
          <TableCell key={4}>{timeStartStr(task)}</TableCell>
          <TableCell key={5}>{timeEndStr(task)}</TableCell>
          <TableCell key={6}>{timeDuration(task)}</TableCell>
          <TableCell key={2}>{statusStr(task)}</TableCell>
          <TableCell key={3}>{progressBar(task)}</TableCell>
          <TableCell key={7}>{taskDownload(task)}</TableCell>
        </TableRow>
      );
    }
    return result;
  }

  //-----------------------------------------------------------------
  function displayBatches() {
    //-----------------------------------------------------------------
    if ( !tasks || Object.keys(tasks).length <= 0 ) return null;

    return (
      <Box { ...BATCHES_CONTAINER } >
        <TableContainer>
          <Typography
            variant='h5'
            sx={{
              margin: '1rem',
            }}
          >
            Tasks Currently Processing
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell key={1}>Batch ID</TableCell>
                <TableCell key={4}>Time Start</TableCell>
                <TableCell key={5}>Time Complete</TableCell>
                <TableCell key={6}>Duration</TableCell>
                <TableCell key={2}>Status</TableCell>
                <TableCell key={3}>Progress</TableCell>
                <TableCell key={7}>Download</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{displayTasks()}</TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  //-----------------------------------------------------------------
  function dislpayUploadProgress() {
    //-----------------------------------------------------------------
    if (loading)
      return (
        <LinearProgress
          variant='determinate'
          color='primary'
          sx={{
            colorPrimary: theme.palette.success.main,
            width: '95%',
            height: '3px',
          }}
          value={uploadProgress}
        />
      );
  };

  //#endregion

  return (
    <Box { ...CONTAINER } >
      {displayInstructions()}
      <Spacer />
      {controls()}
      {dislpayUploadProgress}
      {displayBatches()}
      {displayError()}
    </Box>
  );
}
